import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { api, formatINR, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { OrderStatusTracker } from "@/components/OrderStatusTracker";
import { ChevronDown, ChevronUp, Truck, CheckCircle2, Heart, X } from "lucide-react";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const { ids: wishIds, toggle: toggleWish } = useWishlist();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "orders";
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [user, loading, nav]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/wishlist").then((r) => setWishlist(r.data)).catch(() => {});
  }, [user]);

  // Keep the visible wishlist grid in sync with context (remove instantly when user un-hearts)
  useEffect(() => {
    setWishlist((prev) => prev.filter((p) => wishIds.includes(p.product_id)));
  }, [wishIds]);

  if (loading || !user) return <div className="py-24 text-center text-[#7c7273]">Loading…</div>;

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12" data-testid="account-page">
      <div className="mb-10 flex items-end justify-between">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-2">Namaste</div>
          <h1 className="font-serif text-4xl text-[#2b1b1c]">{user.name}</h1>
          <p className="text-[#7c7273] mt-1">{user.email}</p>
        </div>
        <button className="btn-outline" onClick={() => { logout(); toast.success("Signed out"); nav("/"); }} data-testid="account-logout">Sign out</button>
      </div>

      <div className="border-b border-[#e8e4db] flex gap-8 mb-8">
        {["orders", "wishlist", "profile"].map((t) => (
          <button
            key={t}
            onClick={() => setParams({ tab: t })}
            className={`pb-3 text-sm tracking-[0.2em] uppercase ${tab === t ? "text-[#7a1e2b] border-b-2 border-[#7a1e2b]" : "text-[#7c7273]"}`}
            data-testid={`account-tab-${t}`}
          >{t}</button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="space-y-4">
          {orders.length === 0 ? (
            <p className="font-italic-serif text-xl text-[#7c7273] py-12 text-center">No orders yet. <Link to="/shop" className="link-underline text-[#7a1e2b]">Explore</Link></p>
          ) : orders.map((o) => (
            <div key={o.order_id} className="border border-[#e8e4db] bg-white" data-testid={`order-${o.order_id}`}>
              <button
                className="w-full p-5 flex justify-between items-center hover:bg-[#f5f3ef] transition-colors"
                onClick={() => setExpanded(expanded === o.order_id ? null : o.order_id)}
                data-testid={`order-expand-${o.order_id}`}
              >
                <div className="text-left">
                  <div className="font-serif text-lg">#{o.order_id.slice(-8).toUpperCase()}</div>
                  <div className="text-sm text-[#7c7273]">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} item(s)</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="font-serif text-[#7a1e2b]">{formatINR(o.total)}</div>
                    <div className="text-xs uppercase tracking-widest text-[#7c7273] capitalize">{o.status.replace(/_/g, " ")}</div>
                  </div>
                  {expanded === o.order_id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
              </button>

              {expanded === o.order_id && (
                <div className="p-6 border-t border-[#e8e4db] space-y-6">
                  <OrderStatusTracker status={o.status} statusHistory={o.status_history} />

                  {o.status === "delivered" ? (
                    <div className="flex items-center gap-3 bg-[#4a7a59]/10 border border-[#4a7a59]/30 p-4" data-testid="delivered-banner">
                      <CheckCircle2 size={20} className="text-[#4a7a59]" />
                      <div className="text-sm">
                        <div className="font-medium text-[#4a7a59]">Delivered</div>
                        <div className="text-[#7c7273] text-xs">We hope your saree brings you joy. Do share a picture with us!</div>
                      </div>
                    </div>
                  ) : (["shipped", "out_for_delivery"].includes(o.status) && (o.tracking_number || o.courier)) ? (
                    <div className="flex items-center gap-3 bg-[#f5f3ef] p-4">
                      <Truck size={18} className="text-[#7a1e2b]" />
                      <div className="text-sm">
                        <div className="font-medium">{o.status === "out_for_delivery" ? "Out for delivery today" : "In transit"} with {o.courier || "courier"}</div>
                        {o.tracking_number && <div className="text-[#7c7273] font-mono text-xs">Tracking: {o.tracking_number}</div>}
                      </div>
                    </div>
                  ) : null}

                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#7c7273] mb-2">Items</div>
                    <ul className="divide-y divide-[#e8e4db]">
                      {o.items.map((it) => (
                        <li key={`${it.product_id}::${it.variant_id || "base"}`} className="py-3 flex gap-3 items-center">
                          <img src={imgUrl(it.image)} alt="" className="w-14 h-16 object-cover bg-[#f5f3ef]" />
                          <div className="flex-1">
                            <div className="font-serif">{it.name}</div>
                            <div className="text-sm text-[#7c7273]">{it.color ? `${it.color} · ` : ""}Qty {it.quantity}</div>
                          </div>
                          <div className="text-sm">{formatINR(it.line_total)}</div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6 text-sm">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#7c7273] mb-1">Shipping to</div>
                      <p className="text-[#2b1b1c]">
                        {o.address.full_name}<br/>
                        {o.address.line1}{o.address.line2 ? `, ${o.address.line2}` : ""}<br/>
                        {o.address.city}, {o.address.state} - {o.address.pincode}<br/>
                        {o.address.phone}
                      </p>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#7c7273] mb-1">Payment</div>
                      <div className="text-[#2b1b1c] uppercase">{o.payment_method} · {o.payment_status}</div>
                      <div className="mt-3 space-y-1 text-[#7c7273]">
                        <div>Subtotal: {formatINR(o.subtotal)}</div>
                        <div>Shipping: {o.shipping === 0 ? "Free" : formatINR(o.shipping)}</div>
                        <div>Tax: {formatINR(o.tax)}</div>
                        <div className="font-serif text-[#7a1e2b] text-lg pt-1">Total: {formatINR(o.total)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "wishlist" && (
        <div>
          {wishlist.length === 0 ? (
            <div className="py-16 text-center">
              <Heart size={40} className="mx-auto text-[#c9a34a] mb-4" />
              <p className="font-italic-serif text-xl text-[#7c7273]">Your wishlist is empty.</p>
              <Link to="/shop" className="btn-outline inline-block mt-6" data-testid="wishlist-shop-cta">Browse the edit</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="wishlist-grid">
              {wishlist.map((p) => (
                <div key={p.product_id} className="relative group" data-testid={`wishlist-item-${p.product_id}`}>
                  <Link to={`/product/${p.product_id}`} className="block">
                    <div className="relative overflow-hidden bg-[#f5f3ef] aspect-[3/4]">
                      <img src={imgUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="mt-3 font-serif text-[#2b1b1c]">{p.name}</div>
                    <div className="text-[#7a1e2b]">{formatINR(p.price)}</div>
                  </Link>
                  <button
                    onClick={() => toggleWish(p.product_id, p.name)}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-[#fcfbf8]/95 hover:bg-[#7a1e2b] hover:text-[#fcfbf8] text-[#2b1b1c] flex items-center justify-center shadow-sm transition-colors"
                    aria-label="Remove from wishlist"
                    data-testid={`wishlist-remove-${p.product_id}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "profile" && (
        <div className="max-w-md">
          <div className="space-y-3 text-sm">
            <div><span className="text-[#7c7273]">Name:</span> {user.name}</div>
            <div><span className="text-[#7c7273]">Email:</span> {user.email}</div>
            <div><span className="text-[#7c7273]">Role:</span> <span className="capitalize">{user.role}</span></div>
          </div>
          {user.role === "admin" && (
            <Link to="/admin" className="btn-primary inline-block mt-6" data-testid="goto-admin">Open Admin Dashboard</Link>
          )}
        </div>
      )}
    </div>
  );
}
